import { Skeleton } from 'primereact/skeleton';
import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ValuationOverTime } from '~/api/def/endpoints/portfolioCompanyEndpoint';
import { formatCurrency } from '../money';

interface CurrentValuationBarChartProps {
  data: ValuationOverTime[];
  isLoading: boolean;
  error: Error | null;
  currency: string;
}

export default function CurrentValuationBarChart({
  data,
  error,
  isLoading,
  currency,
}: CurrentValuationBarChartProps) {
  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full"></Skeleton>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 h-full flex justify-content-center align-items-center">
        <div className="p-error">Something went wrong.</div>
      </div>
    );
  }

  const transformedData = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.map((entry) => ({
      name: new Date(entry.valuationDate).toLocaleDateString(),
      value: entry.currentValuation,
    }));
  }, [data]);

  const noData = useMemo(() => {
    return (
      !transformedData.length ||
      transformedData.every((entry) => entry.value === 0)
    );
  }, [transformedData]);

  if (noData) {
    return (
      <div className="flex flex-column h-full align-items-center p-3">
        <div className="flex w-full h-2rem">
          <h3 className="text-lg font-semibold mb-4">
            Current Valuation Over Time
          </h3>
        </div>
        <div className="align-items-center flex h-full mb-6">
          No data found.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <h3 className="text-lg font-semibold mb-4">
        Current Valuation Over Time
      </h3>
      <div className="h-20rem">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            width={500}
            height={300}
            data={transformedData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis
              tickFormatter={(value) => formatCurrency(currency, value, 2)}
            />
            <Tooltip
              formatter={(value) =>
                formatCurrency(currency, value as number, 2)
              }
            />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#8884d8"
              activeDot={{ r: 8 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
